/**
 * UpsellDefaultDeliveryCard — entrega GLOBAL de todos os upsells do bot.
 *
 * É o que "usar entrega global do upsell" significa para cada upsell individual
 * (UpsellForm). Quando esta também está em "padrão", cai no default_delivery do
 * bot (mesmo do fluxo principal).
 */

import { useState, useEffect } from 'react';
import { Layers, RotateCcw, Save } from 'lucide-react';
import DeliveryTypeField from '../../../components/UI/DeliveryTypeSelect/DeliveryTypeField';
import { DELIVERY_TYPE_META } from '../../../utils/deliveryTypeMeta';
import { applyOnlyUpsellRule } from '../../../utils/gateways';
import { useOnlyUpsellBlocked } from '../../../hooks/useOnlyUpsellBlocked';
import { authService } from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';

const DEST_OPTIONS = [
    { value: 'default', label: 'Mesmo do plano principal', icon: RotateCcw, colorClass: DELIVERY_TYPE_META.default.colorClass },
    { value: 'vip_group', label: 'Grupo VIP', ...DELIVERY_TYPE_META.vip_group },
    { value: 'message_only', label: 'Mensagem no Bot', ...DELIVERY_TYPE_META.message_only },
    { value: 'external_link', label: 'Link Externo', ...DELIVERY_TYPE_META.external_link },
    { value: 'only_upsell', label: 'Apenas Upsell', ...DELIVERY_TYPE_META.only_upsell },
];

// Rascunho local até clicar "Salvar" — nada é persistido enquanto o vendedor
// só está trocando o tipo/preenchendo o campo (evita 1 request por clique).
export default function UpsellDefaultDeliveryCard({ botId, value, onChange }) {
    const onlyUpsellBlocked = useOnlyUpsellBlocked(botId);
    const { showError, showSuccess } = useNotification();
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState(value || null);

    useEffect(() => { setDraft(value || null); }, [value]);

    const dirty = JSON.stringify(draft || null) !== JSON.stringify(value || null);

    const save = async () => {
        if (!dirty) return;
        setSaving(true);
        try {
            await authService.upsell.save_default_delivery(botId, draft);
            onChange(draft);
            showSuccess('Entrega global dos upsells atualizada!');
        } catch (err) {
            showError(err?.response?.data?.error || 'Erro ao salvar entrega global do upsell');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="panel-card text-white w-full">
            <div className="p-4 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[rgba(139,92,246,0.1)] border border-[rgba(139,92,246,0.2)] flex items-center justify-center shrink-0">
                        <Layers size={18} className="text-[#a78bfa]" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-white text-sm">Entrega Global dos Upsells</h3>
                        <p className="text-xs text-white/40">Vale para upsells em "usar entrega global do upsell"</p>
                    </div>
                </div>

                <DeliveryTypeField
                    value={draft}
                    onChange={setDraft}
                    options={applyOnlyUpsellRule(DEST_OPTIONS, onlyUpsellBlocked)}
                    showReset
                    resetTitle="Voltar ao padrão (mesmo do plano principal)"
                    panel
                    className={saving ? 'opacity-60 pointer-events-none' : ''}
                />

                {dirty && (
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="btn-primary w-full block mt-4"
                    >
                        <Save size={16} className="inline" /> {saving ? 'Salvando...' : 'Salvar'}
                    </button>
                )}
            </div>
        </div>
    );
}
